/**
 * Canvas Versions Panel (B2.4 backport).
 *
 * Minimal-scope UI for the canvas-versioning feature shipped in B2.1-B2.3:
 *   * Top: "Save current canvas as version…" form (inline input + button).
 *   * List: each saved version with label, timestamp, "Current" badge,
 *     Restore + Delete buttons.
 *   * Errors: surface inline. 409 = project not active → show clear hint.
 *
 * Out of scope for minimal: diff UI, search/filter, branch visualisation,
 * inline rename. Those land later if user engagement justifies the spend.
 *
 * The panel is a floating drawer over the canvas (top-left), toggled by
 * the parent. It does NOT replace the chat panel — both visible at once.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  createVersion,
  deleteVersion,
  listVersions,
  restoreVersion,
  type VersionEntry,
  type VersionListResponse,
} from '@ponpon/api-client'

interface VersionsPanelProps {
  projectId: string
  onClose: () => void
}

export function VersionsPanel({ projectId, onClose }: VersionsPanelProps) {
  const [head, setHead] = useState<string | null>(null)
  const [versions, setVersions] = useState<VersionEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveLabel, setSaveLabel] = useState('')
  const [pendingAction, setPendingAction] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data: VersionListResponse = await listVersions(projectId)
      setHead(data.head)
      setVersions(data.versions)
    } catch (e) {
      setError(toErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleSave = useCallback(async () => {
    const label = saveLabel.trim()
    if (!label) return
    setPendingAction('save')
    setError(null)
    try {
      await createVersion(projectId, label)
      setSaveLabel('')
      await refresh()
    } catch (e) {
      setError(toErrorMessage(e))
    } finally {
      setPendingAction(null)
    }
  }, [projectId, refresh, saveLabel])

  const handleRestore = useCallback(
    async (versionId: string, label: string) => {
      if (
        !window.confirm(
          `Restore canvas to "${label}"?\n\n` +
            'This swaps every node + edge to match the saved state. ' +
            'Cancel any running nodes first if you have them.',
        )
      )
        return
      setPendingAction(`restore:${versionId}`)
      setError(null)
      try {
        await restoreVersion(projectId, versionId)
        await refresh()
      } catch (e) {
        setError(toErrorMessage(e))
      } finally {
        setPendingAction(null)
      }
    },
    [projectId, refresh],
  )

  const handleDelete = useCallback(
    async (versionId: string, label: string) => {
      if (
        !window.confirm(
          `Delete "${label}" and all versions saved after it?\n\n` +
            'This cannot be undone.',
        )
      )
        return
      setPendingAction(`delete:${versionId}`)
      setError(null)
      try {
        await deleteVersion(projectId, versionId)
        await refresh()
      } catch (e) {
        setError(toErrorMessage(e))
      } finally {
        setPendingAction(null)
      }
    },
    [projectId, refresh],
  )

  return (
    <div
      className="absolute top-4 left-4 z-10 w-[340px] max-h-[calc(100%-32px)] flex flex-col rounded-lg border border-line bg-bg-1 shadow-xl"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-line">
        <h3 className="text-[0.88rem] font-medium text-text">Versions</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-text-3 hover:text-text text-[0.84rem] px-2 py-0.5"
          aria-label="Close versions panel"
        >
          ✕
        </button>
      </div>

      <div className="px-4 py-3 border-b border-line">
        <label className="block text-[0.76rem] text-text-2 mb-1.5">
          Save current canvas as…
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={saveLabel}
            onChange={(e) => setSaveLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && saveLabel.trim() && !pendingAction) handleSave()
            }}
            placeholder="e.g. before photoreal switch"
            className="flex-1 min-w-0 px-2 py-1.5 bg-bg border border-line rounded text-[0.82rem] text-text placeholder:text-text-3 focus:outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!saveLabel.trim() || pendingAction !== null}
            className="px-3 py-1.5 bg-accent text-bg rounded text-[0.82rem] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent/90"
          >
            {pendingAction === 'save' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 bg-red-950/40 border border-red-900/60 rounded text-[0.78rem] text-red-200">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading && versions.length === 0 ? (
          <div className="px-2 py-4 text-[0.82rem] text-text-3 text-center">Loading…</div>
        ) : versions.length === 0 ? (
          <div className="px-2 py-4 text-[0.82rem] text-text-3 text-center">
            No saved versions yet. Save one before a risky regen so you can undo.
          </div>
        ) : (
          <ul className="space-y-1">
            {versions
              .slice()
              .reverse() // newest first
              .map((v) => {
                const isCurrent = v.version_id === head
                const restoring = pendingAction === `restore:${v.version_id}`
                const deleting = pendingAction === `delete:${v.version_id}`
                return (
                  <li
                    key={v.version_id}
                    className="px-2 py-2 rounded hover:bg-bg-2 group"
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-[0.84rem] text-text truncate">{v.label}</span>
                      {isCurrent && (
                        <span className="text-[0.66rem] uppercase tracking-wide text-accent">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-[0.7rem] text-text-3 mt-0.5">
                      {formatTimestamp(v.created_at)} · by {v.created_by}
                    </div>
                    <div className="flex gap-2 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => handleRestore(v.version_id, v.label)}
                        disabled={pendingAction !== null || isCurrent}
                        className="text-[0.74rem] text-text-2 hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {restoring ? 'Restoring…' : isCurrent ? 'Current' : 'Restore'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(v.version_id, v.label)}
                        disabled={pendingAction !== null}
                        className="text-[0.74rem] text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {deleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </li>
                )
              })}
          </ul>
        )}
      </div>
    </div>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function toErrorMessage(e: unknown): string {
  if (e instanceof Error) {
    // The api-client throws errors with status-prefixed messages.
    // A 409 means the project room isn't active — pull that out as a
    // clearer hint for the user.
    if (e.message.includes('409')) {
      return "Project isn't active yet — try again in a moment."
    }
    return e.message
  }
  return String(e)
}
